import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  AllocationMedium,
  AllocationMediumApi,
} from 'api/resource-management/types';
import { toastService } from 'components/molecules/toast';
import {
  useEventListenerForEventStore,
  useEventListenerForScheduler,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import { useLoadingRenderer } from 'components/organisms/resource-management/scheduler-config/loading-renderer';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { asApiZoomType } from 'components/organisms/resource-management/utils/common';
import { getCorrectedApiDateRangeBasedOnView } from 'components/organisms/resource-management/utils/dates';
import { getNewRangeExcludingExistingRanges } from 'components/organisms/resource-management/utils/pages';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { defaultFormat } from 'utils/date-field/formatter';
import { queueMacrotask } from 'utils/macrotask';
import { DemandRoleSummary } from '../common-types/demand';
import { Range, SchedulerReference } from '../types';

interface Props {
  schedulerRef: SchedulerReference;
  roleIdList: number[];
  onAllocationLoadHandler: (
    demandSummaries: DemandRoleSummary[],
  ) => Promise<void>;
  currentViewPreset: ViewPresetId;
  workingDaysConfig: WorkingDaysConfiguration;
  showLoaderOnDataFetch?: boolean;
}
interface ResultValue {
  isSchedulerLoadedWithData: boolean;
  rangesLoadedInUI: Range[];
}

export const useDemandLoader = ({
  schedulerRef,
  roleIdList,
  currentViewPreset,
  onAllocationLoadHandler,
  workingDaysConfig,
  showLoaderOnDataFetch = false,
}: Props): ResultValue => {
  const [isSchedulerLoadedWithData, setSchedulerLoadedWithData] =
    useState(false);
  const [rangesLoadedInUI, setRangesLoadedInUI] = useState<Range[]>([]);
  const { showLoading, hideLoading } = useLoadingRenderer({ schedulerRef });

  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');

  const loadDemandForRange = useCallback(
    (/* e */) => {
      const { startDate: correctedFromDate, endDate: correctedToDate } =
        getCorrectedApiDateRangeBasedOnView(
          {
            fromDate: moment(schedulerRef.current.instance.startDate),
            toDate: moment(schedulerRef.current.instance.endDate),
          },
          currentViewPreset,
          workingDaysConfig,
        );

      const currentVisibleDateRange: Range = {
        fromDate: moment(correctedFromDate),
        toDate: moment(correctedToDate),
      };

      const rangeToBeFetched = getNewRangeExcludingExistingRanges(
        rangesLoadedInUI,
        currentVisibleDateRange,
      );

      if (rangeToBeFetched == null) {
        logger.log('** returning as no new rangeToBeFetched');

        return;
      }

      if (showLoaderOnDataFetch) {
        showLoading();
      }

      ResourceManagmentApi.getResourceAllocationDemand({
        startDate: defaultFormat(rangeToBeFetched.fromDate),
        endDate: defaultFormat(rangeToBeFetched.toDate),
        zoomLevel: asApiZoomType(currentViewPreset),
        roleIdList,
      })
        .then((demandData) => {
          if (schedulerRef.current) {
            setRangesLoadedInUI((prev) => [...prev, rangeToBeFetched]);

            const { roleWiseDemandResponses } = demandData;
            const roleWiseDemandSummary: DemandRoleSummary[] =
              roleWiseDemandResponses.reduce(
                (result, { role: { roleId }, demandResponseUnits }) => [
                  ...result,
                  ...demandResponseUnits.map(
                    ({
                      startDate,
                      endDate,
                      capacity,
                      summary: { userSummary, teamSummary },
                    }) => ({
                      roleId,
                      forViewPreset: currentViewPreset,
                      startDate: moment(startDate).startOf('day'),
                      endDate: moment(endDate).endOf('day'),
                      capacity: capacity * 60,
                      usersAllocatedMinutes: {
                        [AllocationMedium.Hard]:
                          userSummary[AllocationMediumApi.Hard],
                        [AllocationMedium.Soft]:
                          userSummary[AllocationMediumApi.Soft],
                      },
                      teamsAllocatedMinutes: {
                        [AllocationMedium.Hard]:
                          teamSummary[AllocationMediumApi.Hard],
                        [AllocationMedium.Soft]:
                          teamSummary[AllocationMediumApi.Soft],
                      },
                    }),
                  ),
                ],
                [] as DemandRoleSummary[],
              );

            onAllocationLoadHandler(roleWiseDemandSummary).then(() => {
              if (!isSchedulerLoadedWithData) {
                setSchedulerLoadedWithData(true);
              }
              if (showLoaderOnDataFetch) {
                hideLoading();
              }
            });
          }
        })
        .catch(() => {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        });
    },
    [
      currentViewPreset,
      errorMessage,
      errorTitle,
      hideLoading,
      isSchedulerLoadedWithData,
      onAllocationLoadHandler,
      rangesLoadedInUI,
      roleIdList,
      schedulerRef,
      showLoaderOnDataFetch,
      showLoading,
      workingDaysConfig,
    ],
  );
  const loadAllocationRef = useRef(loadDemandForRange);

  useEffect(() => {
    loadAllocationRef.current = loadDemandForRange;
  }, [loadDemandForRange]);
  const debouncedLoadAllocations = useMemo(
    () =>
      debounce(
        (e) => {
          switch (e.type.toLowerCase()) {
            case 'loaddaterange':
              break;
            case 'onviewpresetchange':
              setRangesLoadedInUI([]);
              break;
            default:
              break;
          }
          queueMacrotask(() => {
            loadAllocationRef.current();
          });
        },
        500,
        { maxWait: 500 },
      ),
    [],
  );

  useEventListenerForEventStore(schedulerRef, 'loadDateRange', (e) => {
    debouncedLoadAllocations(e);
  });

  useEventListenerForScheduler(schedulerRef, 'onViewPresetChange', (e) => {
    debouncedLoadAllocations(e);
  });

  return {
    isSchedulerLoadedWithData,
    rangesLoadedInUI,
  };
};
